import { appelNatifDisponible, sonnerieNative } from './appelNatif';

/**
 * La tonalite d'attente, cote appelant.
 *
 * Tant que l'autre n'a pas decroche, il ne se passe rien : l'ecran dit
 * « Appel… » et le telephone est muet. On ne sait alors pas si ca part, si
 * ca sonne la-bas, si le reseau tient. Tous les telephones repondent a
 * cette question de la meme facon depuis un siecle — par un bip regulier —
 * et une application qui ne le fait pas a l'air en panne.
 *
 * **Quatre cent quarante hertz, une seconde et demie puis trois secondes
 * et demie de silence** : le retour d'appel francais, celui que l'oreille
 * de nos membres reconnait sans y penser.
 *
 * Deux chemins, comme pour le haut-parleur. Sur iPhone c'est
 * l'application qui joue : une balise audio, meme pour un bip, rendrait la
 * session a WebKit et la route de l'appel avec. Ailleurs, la page
 * fabrique le son elle-meme — un oscillateur et un robinet valent mieux
 * qu'un fichier a charger, a decoder et a tenir a jour.
 */

/** La tonalite du telephone en France. */
const FREQUENCE = 440;
/** Ce qu'on entend, puis ce qu'on attend. */
const TENUE_S = 1.5;
const PERIODE_MS = 5000;
/** Un rappel, pas une sonnerie : elle se joue contre l'oreille. */
const VOLUME = 0.12;
/** Le temps d'ouvrir et de fermer le robinet, sans claquement. */
const FONDU_S = 0.02;

let contexte: AudioContext | null = null;
let oscillateur: OscillatorNode | null = null;
let robinet: GainNode | null = null;
let metronome: number | null = null;

/**
 * Un battement.
 *
 * L'oscillateur tourne sans interruption — le demarrer et l'arreter a
 * chaque bip couterait un noeud par tour — et c'est le robinet qui
 * s'ouvre et se referme. Les rampes sont exponentielles parce qu'une
 * coupure nette sur une sinusoide s'entend comme un clic.
 */
function battre(): void {
  if (!contexte || !robinet) return;
  const t = contexte.currentTime;
  robinet.gain.cancelScheduledValues(t);
  robinet.gain.setValueAtTime(0.0001, t);
  robinet.gain.exponentialRampToValueAtTime(VOLUME, t + FONDU_S);
  robinet.gain.setValueAtTime(VOLUME, t + TENUE_S - FONDU_S);
  robinet.gain.exponentialRampToValueAtTime(0.0001, t + TENUE_S);
}

export async function demarrerLaSonnerie(): Promise<void> {
  if (appelNatifDisponible()) return sonnerieNative(true);
  if (contexte) return;

  try {
    contexte = new AudioContext();
    /*
      Le contexte nait suspendu si le navigateur n'a pas encore vu de
      geste. Ici il en a vu un — on vient d'appuyer sur « appeler » — mais
      le reveil se demande quand meme : un contexte cree pendant que
      l'onglet dormait resterait muet sans un mot.
    */
    await contexte.resume().catch(() => undefined);

    oscillateur = contexte.createOscillator();
    oscillateur.type = 'sine';
    oscillateur.frequency.value = FREQUENCE;

    robinet = contexte.createGain();
    robinet.gain.value = 0.0001;

    oscillateur.connect(robinet).connect(contexte.destination);
    oscillateur.start();

    // Le premier bip tout de suite : attendre cinq secondes donnerait
    // exactement le silence qu'on cherche a combler.
    battre();
    metronome = window.setInterval(battre, PERIODE_MS);
  } catch {
    // Un navigateur qui refuse le son ne doit pas emporter l'appel.
    await arreterLaSonnerie();
  }
}

export async function arreterLaSonnerie(): Promise<void> {
  if (appelNatifDisponible()) return sonnerieNative(false);

  if (metronome !== null) {
    window.clearInterval(metronome);
    metronome = null;
  }
  try {
    oscillateur?.stop();
  } catch {
    /* Deja arrete : un oscillateur ne se rallume pas, il se jette. */
  }
  oscillateur?.disconnect();
  robinet?.disconnect();
  oscillateur = null;
  robinet = null;
  const ancien = contexte;
  contexte = null;
  await ancien?.close().catch(() => undefined);
}
